/**
 * Post-process PCM: tempo (speed) and pitch via OfflineAudioContext.
 * Engines with native speed should pass speed=1 here and apply native instead.
 */

function getWin(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  return g.window || g;
}

/**
 * Apply speed (tempo) and pitch (semitones) to mono Float32 PCM.
 * speed=1, pitchSemitones=0 → returns a copy (or same buffer).
 */
export async function applySpeedPitch(
  pcm: Float32Array,
  sampleRate: number,
  speed: number,
  pitchSemitones: number
): Promise<Float32Array> {
  const s = typeof speed === 'number' && speed > 0 ? speed : 1;
  const p = typeof pitchSemitones === 'number' ? pitchSemitones : 0;
  if (Math.abs(s - 1) < 0.01 && Math.abs(p) < 0.05) {
    return pcm;
  }

  const win = getWin();
  const AC = win.AudioContext || win.webkitAudioContext;
  const OfflineAC = win.OfflineAudioContext || win.webkitOfflineAudioContext;
  if (!AC || !OfflineAC) return pcm;

  // Pitch via playbackRate on a buffer, then time-stretch back for tempo independence.
  // pitchRatio = 2^(semitones/12)
  const pitchRatio = Math.pow(2, p / 12);
  // Effective playback rate for Offline render:
  // We want: output duration = inputDuration / speed
  // and pitch multiplied by pitchRatio.
  // Simple approach: render at rate = speed * pitchRatio, then resample length
  // for pitch-only correction is imperfect; better:
  // 1) Change pitch by resampling (changes duration)
  // 2) Time-stretch to restore / apply speed

  const inputDuration = pcm.length / sampleRate;
  if (inputDuration < 0.01) return pcm;

  // Step 1: pitch shift by resampling (duration becomes duration / pitchRatio)
  const pitchedLen = Math.max(1, Math.floor(pcm.length / pitchRatio));
  const pitched = new Float32Array(pitchedLen);
  for (let i = 0; i < pitchedLen; i++) {
    const src = i * pitchRatio;
    const i0 = Math.floor(src);
    const i1 = Math.min(i0 + 1, pcm.length - 1);
    const t = src - i0;
    pitched[i] = pcm[i0] * (1 - t) + pcm[i1] * t;
  }

  // Step 2: time-stretch to target duration = inputDuration / speed
  // using OfflineAudioContext playbackRate trick (browser WSOLA when preservesPitch available —
  // OfflineAudioContext BufferSource doesn't have preservesPitch, so we use linear
  // time-stretch which is OK for moderate speed changes).
  const targetDuration = inputDuration / s;
  const targetLen = Math.max(1, Math.floor(targetDuration * sampleRate));

  if (Math.abs(targetLen - pitched.length) < 2) {
    return pitched;
  }

  // Linear time-stretch (WSOLA-quality not needed for MVP; SoundTouch CDN optional later)
  const stretched = new Float32Array(targetLen);
  const ratio = pitched.length / targetLen;
  for (let i = 0; i < targetLen; i++) {
    const src = i * ratio;
    const i0 = Math.floor(src);
    const i1 = Math.min(i0 + 1, pitched.length - 1);
    const t = src - i0;
    stretched[i] = pitched[i0] * (1 - t) + pitched[i1] * t;
  }
  return stretched;
}

/**
 * Prefer native speed when engine supports it; apply only residual pitch (and speed if needed).
 */
export async function finalizeAudio(
  pcm: Float32Array,
  sampleRate: number,
  opts: {
    speed: number;
    pitchSemitones: number;
    nativeSpeedApplied?: boolean;
  }
): Promise<{ pcm: Float32Array; sampleRate: number }> {
  const speed = opts.nativeSpeedApplied ? 1 : opts.speed || 1;
  const pitch = opts.pitchSemitones || 0;
  const out = await applySpeedPitch(pcm, sampleRate, speed, pitch);
  return { pcm: out, sampleRate };
}
